// Quests as data (decision 0054: "stages and objectives as data, a journal, trackers"; suggestion: a quest module with
// a cadence kind and a trigger graph). A quest is offered, then active, then done or failed; its objectives are taken
// in order (or all at once when `parallel`), each completing on matching deeds from the event log's vocabulary
// ("defeated" a "wisp" tagged "ruins-guard", four times) or when a condition holds. Actions run when a quest starts,
// when an objective starts or completes, and when a quest is done or failed. Where the quests come from is a
// {@link QuestSource}: the standard one is a fixed table; an AI director can hand out quests it makes (decision 0055).
import type { Condition, FactValue } from "./conditions";
import type { Deed } from "./log";

/** A quest's state. "none": never offered. */
export type QuestStatus = "none" | "offered" | "active" | "done" | "failed";

/** What the story does: set or add to story state, move a quest, an affinity or a standing, have an NPC remember,
 *  move an arc, or ask the game for something only it can do (`host`: give items, spawn enemies, play a scene). */
export type StoryAction =
  | { set: string; value: FactValue }
  | { add: string; amount: number }
  | { quest: string; state: Exclude<QuestStatus, "none"> }
  | { affinity: string; amount: number }
  | { standing: string; amount: number }
  | { remember: string; key: string; value: FactValue }
  | { arc: string; beat: string }
  | { host: string; args?: Record<string, FactValue> };

/** Deeds an objective counts: each given field must match (a list matches any of its values). */
export interface DeedMatch {
  kind: string;
  subject?: string | readonly string[];
  place?: string | readonly string[];
  tag?: string | readonly string[];
  /** How many (default 1). */
  count?: number;
}

/** One objective. */
export interface ObjectiveDef {
  id: string;
  /** Its label (a string id: the game's strings table). */
  label: string;
  /** Completes after `count` matching deeds while it is current… */
  on?: DeedMatch;
  /** …or as soon as this holds while it is current. */
  when?: Condition | string;
  /** Where the objective marker points (the game resolves it: "npc:elder", "place:ruins", "region:town"). */
  marker?: string;
  /** Run when it becomes current, and when it completes. */
  start?: readonly StoryAction[];
  done?: readonly StoryAction[];
}

/** One quest. */
export interface QuestDef {
  id: string;
  /** "main", "side", or any cadence the game uses (daily, friendship…). */
  kind: string;
  title: string;
  summary?: string;
  /** Who gives it (an NPC id), for the journal. */
  giver?: string;
  /** Offered when this holds (checked while "none"); without it, only an action offers it. */
  offer?: Condition | string;
  /** Becomes active as soon as it is offered. */
  autoStart?: boolean;
  /** Fails when this holds while active. */
  fail?: Condition | string;
  /** All objectives current at once (default: one after another). */
  parallel?: boolean;
  objectives: readonly ObjectiveDef[];
  /** Run when it becomes active, done, or failed. */
  start?: readonly StoryAction[];
  rewards?: readonly StoryAction[];
  failed?: readonly StoryAction[];
}

/** Where quests come from. */
export interface QuestSource {
  /** Every quest it has now (an AI source may grow the list as play goes on). */
  list(): readonly QuestDef[];
  get(id: string): QuestDef | undefined;
}

/** The standard source: a fixed table. Throws on duplicate ids, objective ids or an empty quest. */
export function staticQuests(defs: readonly QuestDef[]): QuestSource {
  const byId = new Map<string, QuestDef>();
  for (const q of defs) {
    if (byId.has(q.id)) throw new Error(`quests: "${q.id}" twice`);
    if (!q.objectives.length) throw new Error(`quests: "${q.id}" has no objectives`);
    const seen = new Set<string>();
    for (const o of q.objectives) {
      if (seen.has(o.id)) throw new Error(`quests: "${q.id}" has objective "${o.id}" twice`);
      seen.add(o.id);
      if (!o.on && o.when === undefined) throw new Error(`quests: "${q.id}/${o.id}" completes on nothing (give it \`on\` or \`when\`)`);
    }
    byId.set(q.id, q);
  }
  return { list: () => [...byId.values()], get: (id) => byId.get(id) };
}

/** One quest's progress. */
export interface QuestState {
  status: QuestStatus;
  /** Objectives done, in the order they were. */
  done: string[];
  /** Matching deeds counted so far, per objective. */
  counts: Record<string, number>;
  /** The story clock when it changed status last. */
  since: number;
}

/** The objectives current in a quest: none unless active; else the first not done (or every one not done, parallel). */
export function currentObjectives(def: QuestDef, st: QuestState | undefined): ObjectiveDef[] {
  if (!st || st.status !== "active") return [];
  const left = def.objectives.filter((o) => !st.done.includes(o.id));
  return def.parallel ? left : left.slice(0, 1);
}

/** Whether a deed counts for an objective's match. */
export function deedMatches(m: DeedMatch, d: Deed): boolean {
  const ok = (want: string | readonly string[] | undefined, have: string | undefined): boolean => want === undefined || (typeof want === "string" ? want === have : have !== undefined && want.includes(have));
  return m.kind === d.kind && ok(m.subject, d.subject) && ok(m.place, d.place) && ok(m.tag, d.tag);
}
